import { AddCircleOutline, Delete } from "@mui/icons-material";
import {
  Box,
  Typography,
  Link,
  MenuItem,
  FormControl,
  IconButton,
  Grid,
  TextareaAutosize,
  styled,
} from "@mui/material";
import { AppSelect, InputBase, InputLabel } from "components/common/Input";
import { useEffect, useState } from "react";
import useUpdatePickup from "hooks/pickups/useUpdatePickup";
import usePickup from "hooks/pickups/usePickup";
import theme from "lib/theme";
import { PickupEstimatedItem } from "types/pickupEstimatedItem";
import { useNavigate } from "react-router-dom";
interface PickupEstimatedItemsProps {
  step: number;
  setItemsCompleted: React.Dispatch<React.SetStateAction<boolean>>;
}

const Textarea = styled(TextareaAutosize)({
  "&": {
    padding: "10px",
    fontFamily: "'Catamaran', sans-serif",
    fontSize: "16px",
    resize: "none",
    borderColor: "rgb(206, 212, 218)",
    borderRadius: "5px",
  },
  "&:focus, &:focus-visible": { borderColor: "rgb(0, 137, 189)", outlineColor: "rgb(0, 137, 189)" },
  "&:hover": { borderColor: "rgba(0, 0, 0, 0.87)" },
});

export default function PickupEstimatedItems({ step, setItemsCompleted }: PickupEstimatedItemsProps) {
  let [items, setItems] = useState<PickupEstimatedItem[]>([]);
  const [initialStateLoaded, setInitialStateLoaded] = useState(false);
  const { updatePickup } = useUpdatePickup();
  const { pickup } = usePickup();

  const handleFieldChange = (event: any, index: number, field: string) => {
    let newItems = items;

    newItems = items.map((obj) => {
      if (obj === items[index]) {
        return { ...items[index], [field]: event.target.value };
      }
      return obj;
    });

    setItems(newItems);
  };

  const handleDelete = (index: number) => {
    let newItems = items.filter((_, i) => i !== index);
    setItems(newItems);
  };

  const navigate = useNavigate();

  useEffect(() => {
    if (pickup) {
      const currentItems = pickup.pickupEstimatedItems.map(({ name, quantity, description }) => ({
        name,
        quantity,
        description,
      }));

      setItems(currentItems);
      setInitialStateLoaded(true);
    }
  }, [pickup]);

  useEffect(() => {
    if (initialStateLoaded) {
      updatePickup({ estimatedItems: items });
    }
  }, [initialStateLoaded, items]);

  useEffect(() => {
    if (items.length > 0) {
      setItemsCompleted(true);
    } else {
      setItemsCompleted(false);
    }
  }, [items]);

  return (
    <form
      id="pickup-estimated-items-form"
      onSubmit={(e) => {
        e.preventDefault();
        e.stopPropagation();
        if (pickup?.state === "missing_info") {
          navigate(`/pickups/${pickup.uuid}/summary`);
        } else {
          navigate("/pickups");
        }
        return false;
      }}
    >
      <Box>
        <Typography variant="h6" fontSize="18px" fontWeight="bold">
          {step}. What’s in your pickup?
        </Typography>
        <Typography mt={0.5} variant="body2">
          Let’s breakdown what’s in your pickup. If you’re not exactly sure, take your best guess.
        </Typography>

        <Grid container spacing={1.5} sx={{ mt: 3 }}>
          <Grid item xs={8}>
            <Typography fontWeight={600}>What are we picking up?</Typography>
          </Grid>
          <Grid item xs={4}>
            <Typography fontWeight={600}>Quantity of devices</Typography>
          </Grid>

          {items.map((item, i) => {
            return (
              <>
                <Grid item xs={8}>
                  <FormControl variant="standard" sx={{ width: "100%" }}>
                    <AppSelect
                      id="picking-ups"
                      onChange={(e) => {
                        handleFieldChange(e, i, "name");
                      }}
                      value={item.name}
                    >
                      <MenuItem value={"Apple Laptops"}>Apple Laptops</MenuItem>
                      <MenuItem value={"PC Laptops"}>PC Laptops</MenuItem>
                      <MenuItem value={"Chromebooks"}>Chromebooks</MenuItem>
                      <MenuItem value={"Laptops"}>Laptops Generic</MenuItem>
                      <MenuItem value={"Desktops"}>Desktops</MenuItem>
                      <MenuItem value={"Mac Minis"}>Mac Minis</MenuItem>
                      <MenuItem value={"Monitors"}>Monitors</MenuItem>
                      <MenuItem value={"iPhones"}>iPhones</MenuItem>
                      <MenuItem value={"Mobile Phones"}>Mobile Phones (Non-Apple)</MenuItem>
                      <MenuItem value={"iPads"}>iPads</MenuItem>
                      <MenuItem value={"Tablets"}>Tablets (Non-Apple)</MenuItem>
                      <MenuItem value={"Televisions"}>Televisions</MenuItem>
                      <MenuItem value={"Servers"}>Servers</MenuItem>
                      <MenuItem value={"Server Rack"}>Server Rack</MenuItem>
                      <MenuItem value={"Printers"}>Printers</MenuItem>
                      <MenuItem value={"Small Box"}>
                        Small Box{" "}
                        <Typography color="text.secondary" pl={0.5}>
                          {" "}
                          - 15" L x 12" W x 10" H (in)
                        </Typography>
                      </MenuItem>
                      <MenuItem value={"Large Box"}>
                        Large Box
                        <Typography color="text.secondary" pl={0.5}>
                          - 48" L x 40" W x 36" H (in)
                        </Typography>
                      </MenuItem>
                      <MenuItem value={"Commercial Bin"}>
                        Commercial Bin
                        <Typography color="gray" pl={0.5}>
                          - 48" L x 24" W x 28" H (in)
                        </Typography>
                      </MenuItem>
                      <MenuItem value={"Office Phones"}>Office Phones</MenuItem>
                      <MenuItem value={"Other"}>Other</MenuItem>
                    </AppSelect>
                  </FormControl>
                </Grid>

                <Grid item xs={3}>
                  <FormControl variant="standard" sx={{ width: "100%" }}>
                    <InputBase
                      id="pickups-quantity"
                      type="number"
                      value={item.quantity}
                      onChange={(e) => {
                        handleFieldChange(e, i, "quantity");
                      }}
                      inputProps={{
                        min: 1,
                        onWheel: (e: React.WheelEvent<HTMLInputElement>) =>
                          e.target instanceof HTMLElement && e.target.blur(),
                      }}
                      required
                    />
                  </FormControl>
                </Grid>

                <Grid item xs={1}>
                  <IconButton
                    onClick={() => {
                      handleDelete(i);
                    }}
                  >
                    <Delete />
                  </IconButton>
                </Grid>

                {item.name === "Other" && (
                  <Grid item xs={12} sx={{ pr: "3%", pb: 2 }}>
                    <FormControl variant="standard" sx={{ width: "100%" }}>
                      <InputLabel shrink>Describe this item*</InputLabel>
                      <Textarea
                        id="pickups-description"
                        minRows={4}
                        value={item.description}
                        placeholder="Describe size, type of device, etc."
                        onChange={(e) => {
                          handleFieldChange(e, i, "description");
                        }}
                        required
                      />
                    </FormControl>
                  </Grid>
                )}
              </>
            );
          })}
        </Grid>

        <Link
          component="button"
          type="button"
          onClick={() => {
            setItems([...items, { name: "Apple Laptops", quantity: 1 }]);
          }}
          sx={{
            mt: 1,
            display: "inline-flex",
            alignItems: "center",
            gap: 1,
            fontWeight: 600,
            textDecoration: "none",
            fontSize: "16px",
            fontFamily: "Catamaran",
            ":hover": {
              textDecoration: "underline",
            },
            [theme.breakpoints.down("sm")]: { mt: 2 },
          }}
        >
          <AddCircleOutline sx={{ width: "20px", height: "20px" }} />
          Add items to the pickup
        </Link>

        <Box mt={3} sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}></Box>
      </Box>
    </form>
  );
}
